import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Users,
  Home as HomeIcon,
  Bed,
  Wifi,
  Sparkles,
  MapPin,
  HeartHandshake,
  ArrowRight,
  Quote,
} from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_site-builder-3580/artifacts/8f1fzcms_image.png";

const HERO_IMG =
  "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1600&q=80";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a2e] via-[#240a3d] to-[#2e0e54]" />
          <div className="absolute -top-32 -left-32 w-[520px] h-[520px] glow-purple rounded-full" />
          <div className="absolute -bottom-32 -right-20 w-[520px] h-[520px] glow-gold rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6 fade-up">
              <span className="h-px w-10 bg-[#d4af37]" />
              <span className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">
                Independent Shared Housing
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-[#f7f2e7] fade-up delay-1">
              More than a house.
              <br />
              <span className="gold-text">It's a Haven</span>
              <br />
              for a better tomorrow.
            </h1>
            <p className="mt-7 text-lg text-[#dcd0b3] max-w-xl leading-relaxed fade-up delay-2">
              Beds of Haven offers comfortable, well-maintained homes for adults
              30 and older — a peaceful place to land, heal, and build the
              independent life you deserve.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4 fade-up delay-3">
              <Link to="/apply" data-testid="hero-apply-btn" className="btn-gold">
                Start Your Application <ArrowRight size={18} />
              </Link>
              <Link to="/about" data-testid="hero-learn-btn" className="btn-outline-gold">
                Learn About Us
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-8 fade-up delay-4">
              {[
                { k: "30+", v: "Welcoming ages 30 and older" },
                { k: "24/7", v: "Safety-focused environment" },
                { k: "100%", v: "Furnished & utility-ready" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-serif text-3xl gold-text">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-[#b395d6] mt-1">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative float-slow">
              <div className="absolute -inset-10 rounded-[36px] bg-gradient-to-br from-[#d4af37]/35 via-[#7d3cc8]/20 to-transparent blur-3xl" />
              <div className="relative aspect-square rounded-[32px] overflow-hidden bg-[#f7f2e7] flex items-center justify-center p-6 gold-border shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]">
                <img
                  src={LOGO_URL}
                  alt="Beds of Haven — A Safe Place. A Fresh Start. A Better Future."
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 card-purple px-5 py-3 flex items-center gap-2 whitespace-nowrap">
                <span className="h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
                <span className="font-serif italic text-[#f0d479] text-sm">More than a house. It's a Haven.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAM CARDS */}
      <section className="relative section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">
              Our Programs
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7]">
              Two Havens. <span className="gold-text">One Mission.</span>
            </h2>
            <p className="text-[#dcd0b3] mt-4">
              Dedicated, supportive shared housing for women and men ready to
              take the next step toward independence and growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-14">
            <ProgramCard
              testId="program-card-womens"
              to="/womens"
              title="Women's Haven"
              tagline="She builds. She heals. She rises."
              accent="A Brighter Future."
              image="https://images.unsplash.com/photo-1616137422495-1e9e46e2aa77?auto=format&fit=crop&w=1200&q=80"
              points={[
                "Women ages 30 and older",
                "Peaceful, well-maintained homes",
                "Community of growth & respect",
                "Empowerment toward independence",
              ]}
            />
            <ProgramCard
              testId="program-card-mens"
              to="/mens"
              title="Men's Haven"
              tagline="Focus. Discipline. Growth. Success."
              accent="A Stronger Future."
              image="https://images.unsplash.com/photo-1617104678098-de229db51175?auto=format&fit=crop&w=1200&q=80"
              points={[
                "Men ages 30 and older",
                "Respectful, structured environment",
                "Built for accountability & purpose",
                "Pathway to long-term stability",
              ]}
            />
          </div>
        </div>
      </section>

      {/* SHARED LIVING */}
      <section className="relative section-pad bg-[#15082a]">
        <div className="absolute inset-x-0 top-0 gold-divider" />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">
              Shared Living
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7] leading-tight">
              Stronger <span className="gold-text">together</span>.
            </h2>
            <p className="text-[#dcd0b3] mt-5 leading-relaxed">
              Our shared bedrooms — typically three twin beds per room —
              create a built-in community that's affordable, supportive, and
              never lonely. Each resident gets their own bed and personal
              space, with the encouragement of housemates walking the same
              path forward.
            </p>

            <ul className="mt-7 space-y-3.5">
              {[
                "Your own twin bed with fresh, comfortable bedding",
                "Personal storage for your belongings",
                "Quiet hours and respectful house guidelines",
                "Shared kitchen, living, and laundry spaces",
                "Lower cost — community-style affordable living",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#e8dcc0]">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#d4af37] shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <Link to="/apply" data-testid="shared-living-apply-btn" className="btn-gold mt-9">
              Reserve Your Bed <ArrowRight size={18} />
            </Link>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-[#d4af37]/25 to-transparent blur-2xl" />
            <div className="relative rounded-[24px] overflow-hidden gold-border">
              <img
                src="https://customer-assets.emergentagent.com/job_site-builder-3580/artifacts/y65ic5kv_image.png"
                alt="A bright, welcoming shared bedroom with three twin beds at Beds of Haven"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 card-purple px-5 py-3 flex items-center justify-between">
                <span className="font-serif italic text-[#f0d479]">Three twin beds. One welcoming room.</span>
                <span className="text-xs text-[#b395d6] tracking-wider hidden sm:block">SHARED ROOM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section className="relative section-pad bg-[#15082a]">
        <div className="absolute inset-x-0 top-0 gold-divider" />
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">
                What's Included
              </div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7]">
                Designed for <span className="gold-text">comfort & calm</span>
              </h2>
            </div>
            <p className="text-[#dcd0b3] max-w-md">
              Every Beds of Haven home is move-in ready — from the linens on
              your bed to the Wi-Fi on your phone.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {amenities.map((a) => (
              <div
                key={a.title}
                data-testid={`amenity-${a.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="card-purple p-6 hover:translate-y-[-3px] transition-transform"
              >
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#d4af37] to-[#b8902c] flex items-center justify-center text-[#1a0a2e]">
                  <a.Icon size={20} />
                </div>
                <div className="font-serif text-2xl text-[#f7f2e7] mt-4">{a.title}</div>
                <div className="text-[#cdbfa1] mt-2 text-sm leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE WELCOME */}
      <section className="relative section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[#f0d479] text-xs tracking-[0.32em] uppercase">
              Who We Welcome
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 text-[#f7f2e7]">
              A community built on <span className="gold-text">respect</span>.
            </h2>
            <p className="text-[#dcd0b3] mt-5 leading-relaxed">
              Beds of Haven welcomes adults who are committed to growth,
              stability, and independent living. Our shared homes are sanctuaries
              for those rebuilding, transitioning, and taking the next step
              forward.
            </p>

            <ul className="mt-8 space-y-4">
              {welcome.map((w) => (
                <li key={w} className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rotate-45 bg-[#d4af37]" />
                  <span className="text-[#e8dcc0]">{w}</span>
                </li>
              ))}
            </ul>

            <Link to="/apply" data-testid="welcome-apply-btn" className="btn-gold mt-9">
              Apply Today <ArrowRight size={18} />
            </Link>
          </div>

          <div className="relative">
            <div className="card-purple p-10">
              <Quote className="text-[#d4af37]" size={36} />
              <p className="font-serif text-2xl md:text-3xl text-[#f7f2e7] mt-5 leading-snug">
                "A safe place changed everything. Beds of Haven gave me the
                steady ground I needed to start again — and a community that
                actually believed I could."
              </p>
              <div className="mt-7 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#d4af37] to-[#b8902c]" />
                <div>
                  <div className="text-[#f0d479] font-medium">A Resident's Story</div>
                  <div className="text-xs text-[#b395d6] tracking-wider">Women's Haven · 2025</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative section-pad bg-gradient-to-br from-[#240a3d] to-[#3d1465]">
        <div className="absolute inset-0 grain" />
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center relative">
          <HeartHandshake className="mx-auto text-[#d4af37]" size={42} />
          <h2 className="font-serif text-4xl md:text-5xl mt-5 text-[#f7f2e7]">
            Your fresh start <span className="gold-text">begins here.</span>
          </h2>
          <p className="text-[#dcd0b3] mt-5 max-w-2xl mx-auto">
            If you're 30 or older, have verified income, and you're ready to
            commit to your next chapter — we'd love to meet you.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/apply" data-testid="cta-apply-btn" className="btn-gold">
              Apply Now <ArrowRight size={18} />
            </Link>
            <a href="tel:7064318996" data-testid="cta-call-btn" className="btn-outline-gold">
              Call 706-431-8996
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProgramCard({ to, title, tagline, accent, image, points, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative block overflow-hidden rounded-[22px] gold-border bg-[#1a0a2e]"
    >
      <div className="relative h-72 overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a0a2e] via-[#1a0a2e]/40 to-transparent" />
        <div className="absolute top-5 left-5 text-[10px] tracking-[0.3em] uppercase text-[#f0d479] bg-[#1a0a2e]/70 px-3 py-1.5 rounded-full gold-border">
          Independent Shared Housing
        </div>
      </div>
      <div className="p-7">
        <div className="font-serif text-3xl text-[#f7f2e7]">{title}</div>
        <div className="text-[#b395d6] text-sm tracking-wide mt-1">{tagline}</div>
        <div className="font-serif italic gold-text text-xl mt-3">{accent}</div>

        <ul className="mt-6 space-y-2.5">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2 text-[#e8dcc0] text-sm">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#d4af37]" />
              {p}
            </li>
          ))}
        </ul>

        <div className="mt-7 inline-flex items-center gap-2 text-[#f0d479] group-hover:gap-3 transition-all">
          Explore {title} <ArrowRight size={18} />
        </div>
      </div>
    </Link>
  );
}

const amenities = [
  { title: "Fully Furnished", desc: "Private or shared room options, beds made, ready the day you arrive.", Icon: Bed },
  { title: "Utilities & Wi-Fi", desc: "Power, water, and high-speed internet — all included, no surprises.", Icon: Wifi },
  { title: "On-Site Laundry", desc: "Convenient laundry access without leaving the comfort of home.", Icon: Sparkles },
  { title: "Comfortable Common Areas", desc: "Clean, welcoming shared spaces designed for rest and connection.", Icon: HomeIcon },
  { title: "Convenient Locations", desc: "Near transportation and essential services — life within reach.", Icon: MapPin },
  { title: "Safe & Supported", desc: "24/7 safety-focused environment with a respectful, structured culture.", Icon: ShieldCheck },
];

const welcome = [
  "Adults age 30 and older",
  "Seeking stable, affordable housing",
  "Transitioning from life challenges",
  "Must have verified, guaranteed income",
  "Committed to personal growth and independent living",
];
